import { action, computed, makeObservable, observable } from 'mobx';

export const PopupPriority = {
  NO_ACTIVE_PACKAGE: 0,
  STREAK: 1,
  EVENTS: 2,
  CHECKIN_SUGGESTION: 3,
} as const;

/**
 * Arbitrates one-at-a-time display of competing popups by priority
 * (lower number = higher priority). The active popup is always the
 * highest-priority pending request; lower-priority popups stay hidden
 * (yielding) until it unregisters, at which point the next one becomes
 * active. This lets popups play out as a chain, e.g. streak reward →
 * events ad → check-in suggestion.
 *
 * Once-per-session semantics are owned by each popup component (via its
 * own show/fetch state), so a dismissed popup simply does not register
 * itself again — the store does not need a global lock.
 */
export class PopupStore {
  @observable private requests: Map<string, number> = new Map();

  constructor() {
    makeObservable(this);
  }

  @action
  register = (id: string, priority: number) => {
    this.requests.set(id, priority);
  };

  @action
  unregister = (id: string) => {
    this.requests.delete(id);
  };

  @computed
  get activePopupId(): string | null {
    let activeId: string | null = null;
    let bestPriority = Number.POSITIVE_INFINITY;
    this.requests.forEach((priority, id) => {
      if (priority < bestPriority) {
        bestPriority = priority;
        activeId = id;
      }
    });
    return activeId;
  }

  isActive = (id: string): boolean => {
    return this.activePopupId === id;
  };

  // True when another (higher-priority) popup currently holds the active
  // slot. Use this to decide whether to stay hidden while still registered.
  // It is false before any popup has registered, so first-render checks
  // don't mis-fire while effects are still pending.
  isYielding = (id: string): boolean => {
    const active = this.activePopupId;
    return active !== null && active !== id;
  };
}

export default PopupStore;
